// Option data + prompt sentinels for the guided Character Builder.

export const NAME_OPTIONS = [
  "Name It for Me",
  "Emberfang",
  "Stormwing",
  "Mossback",
  "Moonwhisper",
  "Shadowclaw",
  "Prismtail",
  "Thundercrest",
  "Starfire",
  "Custom Name…",
];

export const CREATURE_TYPES = [
  "Let AI Identify It From the Drawing",
  "Dinosaur",
  "Dragon",
  "Fantasy Animal",
  "Magical Creature",
  "Friendly Monster",
  "Fierce Monster",
  "Superhero",
  "Guardian",
  "Knight",
  "Fairy",
  "Mermaid or Sea Creature",
  "Robot",
  "Alien",
  "Mythical Hybrid",
  "Talking Animal",
  "Living Plant or Tree Creature",
  "Vehicle Character",
  "Surprise Me",
  "Custom Creature…",
];

export const PERSONALITY = [
  "Brave", "Friendly", "Funny", "Kind-hearted", "Fierce Protector", "Loyal", "Gentle",
  "Curious", "Mischievous", "Wise", "Shy", "Adventurous", "Fearless", "Mysterious",
  "Playful", "Clever", "Surprise Me", "Custom Personality…",
];

export const ROLES = [
  "Guardian of the Forest", "Protector of Smaller Creatures", "Hero of the Kingdom",
  "Brave Adventurer", "Magical Healer", "Explorer of Lost Worlds", "Keeper of the Elements",
  "Defender of the Ocean", "Guardian of Dreams", "Protector of the Stars", "Inventor",
  "Team Leader", "Loyal Companion", "Friendly Trickster", "Reluctant Hero",
  "Villain Who Becomes a Hero", "Surprise Me", "Custom Role…",
];

// Pet-specific fantasy forms + roles, shown when the uploaded subject is an animal/pet.
export const PET_CREATURE_TYPES = [
  "Let AI Choose the Best Fantasy Form",
  "Legendary Guardian Beast",
  "Mythical Companion",
  "Elemental Spirit Animal",
  "Winged Familiar",
  "Armored War Beast",
  "Celestial Creature",
  "Enchanted Forest Guardian",
  "Dragon-Kin",
  "Mystic Shapeshifter",
  "Noble Royal Steed",
  "Cuddly Magical Critter",
  "Surprise Me",
  "Custom Creature…",
];

export const PET_ROLES = [
  "Loyal Companion", "Guardian of its Family", "Brave Protector", "Magical Familiar",
  "Forest Spirit Guide", "Keeper of Ancient Magic", "Playful Trickster", "Noble Steed",
  "Healer's Companion", "Legendary Beast of the Realm", "Explorer's Sidekick",
  "Surprise Me", "Custom Role…",
];

export const PET_DEFAULT_TYPE = "Let AI Choose the Best Fantasy Form";

export const APPEARANCE = [
  "Preserve All Important Details From My Drawing", "Preserve the Original Colors",
  "Armored Scales", "Soft Fur", "Feathers", "Glowing Eyes", "Large Wings", "Small Wings",
  "Horns", "Spikes", "Long Tail", "Powerful Claws", "Magical Markings", "Crystal Armor",
  "Nature or Plant Details", "Fire Details", "Ice Details", "Water Details", "Lightning Details",
  "Rainbow Colors", "Metallic Details", "Battle Armor", "Cape", "Crown",
  "Surprise Me", "Custom Appearance Detail…",
];

export const POWERS = [
  "Flight", "Super Strength", "Super Speed", "Healing", "Protective Shield", "Fire Breath",
  "Ice Breath", "Lightning", "Water Control", "Nature Control", "Camouflage", "Invisibility",
  "Teleportation", "Shape-Shifting", "Powerful Roar", "Night Vision", "Talking to Animals",
  "Weather Control", "Magic", "No Special Powers", "Surprise Me", "Custom Power…",
];

export const BACKGROUNDS = [
  "Magical Forest", "Prehistoric Jungle", "Ancient Mountain", "Castle Kingdom", "Peaceful Meadow",
  "Mysterious Cave", "Ocean or Underwater World", "Outer Space", "Alien Planet", "Enchanted Village",
  "Dramatic Sunset", "Stormy Battlefield", "Snowy Wilderness", "Desert Ruins", "Simple Studio Background",
  "Background That Best Fits the Drawing", "Surprise Me", "Custom Setting…",
];

export const POSES = [
  "Heroic Standing Pose", "Walking Toward the Camera", "Flying", "Running", "Roaring",
  "Protecting Another Creature", "Using Its Powers", "Exploring", "Resting Peacefully", "Playful Pose",
  "Keep the Pose From My Drawing", "Pose That Best Fits the Character", "Surprise Me", "Custom Pose…",
];

export const BACKSTORY = [
  "Let AI Create the Backstory", "Born to Protect the Weak", "Discovered Inside a Mysterious Egg",
  "Guardian of an Ancient Forest", "Last of a Legendary Species", "Searching for a Lost Friend",
  "Learning to Control New Powers", "Defender of a Hidden Kingdom", "Created by a Young Inventor",
  "Visitor From Another World", "Former Villain Becoming a Hero", "Part of a Team of Guardians",
  "No Backstory", "Custom Backstory…",
];

// id must match the human-readable value stored + interpreted server-side.
export const STYLE_CARDS = [
  { id: "Realistic Cinematic", icon: "Film", grad: "from-[#4A235A] to-[#2A1533]",
    desc: "Highly detailed textures, dramatic lighting and a movie-quality fantasy appearance." },
  { id: "3D Animated", icon: "Boxes", grad: "from-[#9B5DE5] to-[#5B2A86]",
    desc: "Polished, colorful and expressive, like a modern family animated adventure." },
  { id: "Classic Cartoon", icon: "Smile", grad: "from-[#F4D03F] to-[#E67E22]",
    desc: "Bold outlines, bright colors and a fun, friendly cartoon appearance." },
  { id: "Storybook Illustration", icon: "BookOpen", grad: "from-[#E8A87C] to-[#C38D9E]",
    desc: "Warm, imaginative artwork suitable for a children's fantasy book." },
  { id: "Epic Fantasy Painting", icon: "Swords", grad: "from-[#2C3E50] to-[#4A235A]",
    desc: "Richly painted details, magical lighting and an adventurous fantasy-world atmosphere." },
  { id: "Comic Book", icon: "Zap", grad: "from-[#E74C3C] to-[#8E44AD]",
    desc: "Dynamic ink lines, strong colors, dramatic action and graphic-novel energy." },
  { id: "Anime-Inspired", icon: "Sparkles", grad: "from-[#5DADE2] to-[#9B59B6]",
    desc: "Expressive features, clean linework and energetic fantasy styling." },
  { id: "Watercolor Fantasy", icon: "Palette", grad: "from-[#76D7C4] to-[#5DADE2]",
    desc: "Soft painted color, artistic texture and a magical handcrafted feeling." },
  { id: "Preserve My Drawing's Style", icon: "Pencil", grad: "from-[#95A5A6] to-[#5D6D7E]",
    desc: "Polish and enhance the original artwork while staying close to its hand-drawn look." },
  { id: "Surprise Me", icon: "Shuffle", grad: "from-[#F4D03F] via-[#9B5DE5] to-[#4A235A]",
    desc: "Let AI choose the style that best fits your uploaded drawing and details." },
];

// character-detail fields rendered by the builder (style, name and backstory handled separately)
export const DETAIL_FIELDS = [
  { key: "type", label: "What kind of character is it?", kind: "single",
    options: CREATURE_TYPES, custom: "Custom Creature…", placeholder: "Choose a creature type" },
  { key: "personality", label: "Personality", kind: "multi", max: 3,
    options: PERSONALITY, custom: "Custom Personality…", placeholder: "Pick up to 3" },
  { key: "role", label: "Their role", kind: "single",
    options: ROLES, custom: "Custom Role…", placeholder: "Choose a role" },
  { key: "appearance", label: "Special appearance details", kind: "multi", max: 5,
    options: APPEARANCE, custom: "Custom Appearance Detail…", placeholder: "Pick up to 5" },
  { key: "powers", label: "Powers & abilities", kind: "multi", max: 3,
    options: POWERS, custom: "Custom Power…", placeholder: "Pick up to 3" },
  { key: "background", label: "Where should your character appear?", kind: "single",
    options: BACKGROUNDS, custom: "Custom Setting…", placeholder: "Choose a background" },
  { key: "pose", label: "What should the character be doing?", kind: "single",
    options: POSES, custom: "Custom Pose…", placeholder: "Choose a pose" },
];

export const CUSTOM_SUFFIX = "…";
export const isCustomOption = (o) => typeof o === "string" && o.endsWith(CUSTOM_SUFFIX) && o.toLowerCase().startsWith("custom");

export const DEFAULTS = {
  name: "Name It for Me",
  type: "Let AI Identify It From the Drawing",
  visual_style: "Realistic Cinematic",
  personality: [],
  role: "",
  appearance: ["Preserve All Important Details From My Drawing"],
  powers: [],
  background: "Background That Best Fits the Drawing",
  pose: "",
  backstory: "Let AI Create the Backstory",
};
